var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useMemo } from 'react';
import { translate } from '@utils/i18n';
import { AttachmentsSurface } from '@features/Attachments/AttachmentsSurface';
import { buildFileTypeOptions } from '@features/Attachments/utils/filterOptions';
import { InlineNameCell } from '@features/Attachments/components/InlineNameCell';
import { Text } from '@ui';
import { bulkDestroyAttachments, destroyAttachment, fetchWorkOrderAttachments, updateWorkOrderAttachment } from './services/workOrderAttachmentsApi';
import { createDirectUploadTransport } from './services/directUploadTransport';
import { SupportDocumentActionCell } from './components/SupportDocumentActionCell';
import { toWorkOrderAttachmentRow } from './utils/toWorkOrderAttachmentRow';
import { buildWorkOrderColumns, WORK_ORDER_SORTABLE_COLUMNS } from './utils/workOrderColumns';
import { buildSourceOptions } from './utils/filterOptions';
import { WORK_ORDER_DEFAULT_UPLOAD_ATTRIBUTES, buildAddAttachmentsColumns, buildAddAttachmentsLabels, toWorkOrderQueueFields } from './utils/addAttachmentsConfig';
import { COMMONS_NS, EDIT_MODAL_ERRORS_NS, MASS_ACTIONS_NS, PROGRESS_TOAST_NS, REMOVE_KIND_UNLINK, SOURCE_FILTER_KEY, UPLOAD_NS, WORK_ORDER_ATTACHMENTS_ENTITY_KEY, WORK_ORDER_ATTACHMENTS_NS } from './constants';
/**
 * Work Order "Support Documentation" as a configuration of the shared `AttachmentsSurface`.
 *
 * The one thing that makes this surface unlike Building's and Inspection's: its rows are NOT all
 * the same kind of record. Uploaded attachments sit alongside generated reports, inspection
 * documents and proposal documents, which are join rows pointing at documents owned elsewhere.
 * Those three are read-only, unselectable, and removed by unlinking rather than destroying — which
 * is why this is the first surface to use `isRowSelectable` and `renderRowAction`.
 *
 * No `renderBulkActions`: the only mass edit worth having here is "Show on report", and with a
 * single checkbox the row-level control is the simpler path. Delete is the shared toolbar's.
 */
export function WorkOrderAttachmentsSurface({ workOrderId, onAddAttachment, maxUploadSizeBytes, acceptContentTypes }) {
    const uploadLabels = useMemo(() => buildAddAttachmentsLabels(maxUploadSizeBytes !== null && maxUploadSizeBytes !== void 0 ? maxUploadSizeBytes : DEFAULT_MAX_UPLOAD_SIZE_BYTES), [maxUploadSizeBytes]);
    const accept = useMemo(() => acceptContentTypes === null || acceptContentTypes === void 0 ? void 0 : acceptContentTypes.join(','), [acceptContentTypes]);
    const config = useMemo(() => ({
        entityKey: WORK_ORDER_ATTACHMENTS_ENTITY_KEY,
        namespace: WORK_ORDER_ATTACHMENTS_NS,
        progressToastNamespace: PROGRESS_TOAST_NS,
        className: 'work-order-attachments-table',
        data: {
            fetchAll: fetchWorkOrderAttachments,
            toRow: toWorkOrderAttachmentRow,
            destroyOne: destroyAttachment,
            destroyMany: bulkDestroyAttachments
        },
        labels: {
            deleteSuccessToast: (count) => translate('toasts.delete_success', { namespace: MASS_ACTIONS_NS, count }),
            deleteErrorToast: translate('toasts.delete_error', { namespace: MASS_ACTIONS_NS }),
            deleteConfirmBulkTitle: (count) => translate('delete_confirm.bulk_title', { namespace: MASS_ACTIONS_NS, count }),
            deleteConfirmSingleTitle: (fileName) => translate('delete_confirm.single_title', { namespace: MASS_ACTIONS_NS, fileName }),
            deleteConfirmDescription: translate('delete_confirm.description', { namespace: MASS_ACTIONS_NS }),
            deleteConfirmLabel: translate('delete_confirm.confirm_label', { namespace: MASS_ACTIONS_NS }),
            deleteCancelLabel: translate('cancel', { namespace: COMMONS_NS }),
            rowDeleteAriaLabel: (fileName) => translate('row_delete_aria_label', { namespace: MASS_ACTIONS_NS, fileName }),
            unlink: {
                successToast: translate('toasts.unlink_success', { namespace: MASS_ACTIONS_NS }),
                errorToast: translate('toasts.unlink_error', { namespace: MASS_ACTIONS_NS }),
                confirmTitle: (fileName) => translate('unlink_confirm.title', { namespace: MASS_ACTIONS_NS, fileName }),
                confirmDescription: translate('unlink_confirm.description', { namespace: MASS_ACTIONS_NS }),
                confirmLabel: translate('unlink_confirm.confirm_label', { namespace: MASS_ACTIONS_NS })
            }
        },
        isRowUnlink: (row) => row.removeKind === REMOVE_KIND_UNLINK,
        // A generated document's name belongs to the Report or Proposal behind it, so it renders as
        // plain text. Editing it here would either be discarded or rename a record this screen does
        // not own.
        renderNameCell: (row) => row.editable ? (React.createElement(InlineNameCell, { name: row.name, commitName: (nextName) => __awaiter(this, void 0, void 0, function* () {
                try {
                    const updated = yield updateWorkOrderAttachment(workOrderId, row.id, { name: nextName });
                    return { success: true, name: updated.name };
                }
                catch (_a) {
                    return { success: false, error: translate('generic', { namespace: EDIT_MODAL_ERRORS_NS }) };
                }
            }) })) : (React.createElement(Text, { variant: "body" }, row.name)),
        entityColumns: ({ t: surfaceT }) => buildWorkOrderColumns({ translateFn: surfaceT, workOrderId }),
        entitySortableColumns: WORK_ORDER_SORTABLE_COLUMNS,
        filters: ({ t: surfaceT }) => [
            {
                key: SOURCE_FILTER_KEY,
                label: surfaceT('filters.source_label'),
                options: () => buildSourceOptions(surfaceT),
                getValues: (row) => [row.source]
            },
            {
                key: 'file_type',
                label: surfaceT('filters.file_type_label'),
                options: buildFileTypeOptions,
                getValues: (row) => [row.typeFilterValue]
            }
        ],
        searchableText: (row) => [row.name, row.sourceLabel],
        // Only uploaded attachments may be selected: every bulk action this surface offers writes to
        // `work_order_attachments`, and a selected report row could only ever be a no-op or an error.
        isRowSelectable: (row) => row.selectable,
        renderRowAction: (row, { requestDelete }) => (React.createElement(SupportDocumentActionCell, { item: row, onRequestRemove: requestDelete, deleteAriaLabel: (fileName) => translate('row_delete_aria_label', { namespace: MASS_ACTIONS_NS, fileName }), unlinkAriaLabel: (fileName) => translate('row_unlink_aria_label', { namespace: MASS_ACTIONS_NS, fileName }) })),
        upload: {
            transport: createDirectUploadTransport,
            defaultAttributes: WORK_ORDER_DEFAULT_UPLOAD_ATTRIBUTES,
            toFields: toWorkOrderQueueFields,
            columns: ({ updateItemMetadata }) => buildAddAttachmentsColumns(updateItemMetadata),
            labels: uploadLabels,
            accept,
            successToast: (count) => translate('toasts.success', { namespace: UPLOAD_NS, count })
        }
    }), [workOrderId, uploadLabels, accept]);
    return (React.createElement(AttachmentsSurface, { config: config, ownerId: workOrderId, maxUploadSizeBytes: maxUploadSizeBytes, onAddAttachment: onAddAttachment }));
}
/** Mirrors the surface's own fallback — see `AttachmentsSurface`'s constant of the same name. */
const DEFAULT_MAX_UPLOAD_SIZE_BYTES = 150 * 1024 * 1024;
